import React, { useEffect, useState } from "react";

import {
  Text,
  Toast,
  Button,
  EditModal,
  InputLabel,
  AttrPicker,
  GroupInfoBox,
  ConfirmAlert,
  AddItemButton,
  DeleteAlertComponent,
} from "components";

import { useFlag } from "hooks";
import { changeArrayAt } from "utils";
import { colors, sizes, strings } from "assets/constants";
import { View } from "react-native";
import css from "styles";
import { useAttribute } from "clients/apis";

export default function PopularAttrs(props: {
  attrs: AttributeT[];
  onChange: (attrs: AttributeT[]) => void;
  disabled?: boolean;
}) {
  const { attrs, onChange, disabled } = props;
  const [itemsState, setItemsState] = useState<Array<AttributeT>>(attrs);
  useEffect(() => {
    setItemsState(attrs);
  }, [attrs]);

  const [indexToEdit, setIndexToEdit] = useState<number | null>(null);
  const { flg: modal, on: showModal, off: hideModal } = useFlag();
  const { flg: alert, on: showAlert, off: hideAlert } = useFlag();

  const [selectedAttr, setSelectedAttr] = useState<AttributeT | undefined>();
  const [selectedAttrValues, setSelectedAttrValues] = useState<
    AttributeItemT[] | undefined
  >();
  const { refetch } = useAttribute(selectedAttr?.attribute_id);

  useEffect(() => {
    typeof indexToEdit === "number" &&
      (selectedAttr?.id || selectedAttr?.attribute_id) &&
      refetch().then((res) => {
        if (res.status === "success") {
          setSelectedAttrValues(res.data.data.items);
        }
      });
  }, [selectedAttr, refetch, indexToEdit]);

  const [fields, setFields] = useState<AttributeT>();
  const updateFields = (newVal: AttributeT) =>
    setFields((prev) => ({ ...prev, ...newVal }));

  const _onPressAttrValue = (item: any) => {
    const itemIndex = fields?.items?.findIndex((e) => e.id === item.id);
    let tmp = fields?.items || [];
    if (typeof itemIndex === "number" && itemIndex > -1) {
      tmp = changeArrayAt(tmp, itemIndex);
    } else {
      tmp?.push({ ...item });
    }
    updateFields({ items: tmp });
  };

  const resetForm = () => {
    setIndexToEdit(null);
    setSelectedAttr(undefined);
    setSelectedAttr(undefined);
    setSelectedAttrValues(undefined);
    setFields({});
  };

  const _onAdd = () => {
    fields && onChange(attrs ? [...attrs, fields] : [fields]);
    resetForm();
  };

  const _onEdit = () => {
    const tmp =
      typeof indexToEdit === "number" &&
      changeArrayAt(itemsState, indexToEdit, fields);
    tmp && onChange(tmp);
  };

  const _onDelete = (close: () => void) => {
    const tmp =
      typeof indexToEdit === "number" && changeArrayAt(itemsState, indexToEdit);
    tmp && onChange(tmp);
    resetForm();
    close();
  };

  const _onChangeAttr = (val?: PickerItemT) => {
    updateFields({ title: val?.title, id: val?.id });
    setSelectedAttr(val);
    setSelectedAttrValues(val?.items);
  };

  const _onConfirm = () => {
    if (
      typeof indexToEdit !== "number" &&
      attrs?.findIndex(
        (a) => selectedAttr?.id === a.id || selectedAttr?.id === a.attribute_id
      ) > -1
    ) {
      Toast.warning({
        message: "این ویژگی قبلا انتخاب شده است",
      });
      return;
    }

    if (!fields?.items || fields?.items?.length === 0) {
      Toast.warning({ message: strings.fill_all_fields });
      return;
    }

    indexToEdit === null ? _onAdd() : _onEdit();
    resetForm();
    hideModal();
  };

  const _onCancel = () => {
    resetForm();
    hideModal();
  };

  const _onSelectAttrWarning = () => {
    Toast.warning({ message: "نام ویژگی قابل ویرایش نیست" });
  };

  return (
    <>
      <View style={css.responsiveListContainer}>
        {itemsState?.length > 0 ? (
          itemsState?.map((attr: AttributeT, key: number) => {
            return (
              <View key={String(key)} style={css.responsiveBoxContainer}>
                <GroupInfoBox
                  disabled={disabled}
                  onEdit={() => {
                    setSelectedAttr(attr);
                    setIndexToEdit(key);
                    setFields(attr);
                    showModal();
                  }}
                  onDelete={() => {
                    setIndexToEdit(key);
                    showAlert();
                  }}
                  title={attr?.title}
                  icon={{ name: "list" }}
                  renderContent={() => (
                    <Text style={{ lineHeight: 22 }}>
                      {attr.items?.map((e) => e.value).join(" , ")}
                    </Text>
                  )}
                />
              </View>
            );
          })
        ) : (
          <Text
            size="sm"
            color={colors.textColorGray}
            style={{ marginBottom: sizes.space / 2 }}
          >
            {strings.no_attr_customer}
          </Text>
        )}
      </View>

      {!disabled && <AddItemButton onPress={showModal} />}

      <EditModal
        visible={modal}
        onCancel={_onCancel}
        onConfirm={_onConfirm}
        renderForm={() => {
          return (
            <>
              <InputLabel label={strings.attribute} />
              <AttrPicker
                hideError
                value={fields}
                onSelect={_onChangeAttr}
                onPress={
                  typeof indexToEdit === "number"
                    ? _onSelectAttrWarning
                    : undefined
                }
              />
              <View style={{ marginBottom: sizes.space }} />
              {selectedAttrValues && selectedAttrValues?.length > 0 && (
                <>
                  <Text>مقادیر دلخواه را انتخاب کنید</Text>
                  <View
                    style={{
                      ...css.wrap,
                      ...css.rRowCenterAlign,
                      marginBottom: sizes.space,
                    }}
                  >
                    {selectedAttrValues?.map((item, key) => {
                      return (
                        <Button
                          outline={
                            !(
                              fields?.items &&
                              fields?.items?.findIndex(
                                (e) => e.id === item.id
                              ) > -1
                            )
                          }
                          onPress={() => _onPressAttrValue(item)}
                          style={{
                            paddingHorizontal: sizes.space,
                            marginLeft: sizes.space / 2,
                            marginTop: sizes.space,
                          }}
                          width={"auto"}
                          height={25}
                          key={String(key)}
                          title={{ value: item?.value, size: "sm" }}
                        />
                      );
                    })}
                  </View>
                </>
              )}
            </>
          );
        }}
      />

      <ConfirmAlert
        force
        show={alert}
        hide={hideAlert}
        renderContent={(close) => (
          <DeleteAlertComponent
            removeLoading={false}
            remove={() => _onDelete(close)}
            cancel={close}
          />
        )}
      />
    </>
  );
}
